/**
 * Prototype schema fixtures used as a fallback when the backend schema API is unavailable.
 */
import type { FormSchema, SchemaMetadata, SchemaVersion } from './services/schemaTypes';

const baseMetadata = {
  version: 1,
  legalPracticeId: 'prototype',
  status: 'published' as const,
  usageCount: 0,
  createdAt: '2024-01-01T00:00:00.000Z',
  updatedAt: '2024-01-01T00:00:00.000Z',
  createdBy: 'prototype@example.com',
  updatedBy: 'prototype@example.com',
};

const prototypeSchemas: Record<string, FormSchema> = {
  'prototype-client-intake': {
    id: 'prototype-client-intake',
    ...baseMetadata,
    title: 'New Client Intake Form',
    description: 'Confidential form to collect initial information about a new client and matter.',
    type: 'object',
    required: ['clientName', 'matterType', 'termsAgreement'],
    properties: {
      clientName: {
        type: 'string',
        title: "Client's Full Name",
      },
      clientContact: {
        type: 'object',
        title: 'Client Contact Information',
        properties: {
          email: {
            type: 'string',
            format: 'email',
            title: 'Email Address',
          },
          phone: {
            type: 'string',
            title: 'Phone Number',
            repeatable: true,
          },
        },
        required: ['email'],
      },
      matterType: {
        type: 'string',
        title: 'Type of Legal Matter',
        enum: ['Litigation', 'Transactional', 'Advisory', 'Corporate'],
      },
      servicesOfInterest: {
        type: 'array',
        title: 'Which services are you interested in?',
        description: 'Select all matter types that may apply.',
        items: {
          type: 'string',
          enum: ['Contract drafting', 'Regulatory compliance', 'Trademark registration', 'Employment policies', 'M&A support'],
        },
        widget: 'multiSelect',
      },
      matterDescription: {
        type: 'string',
        title: 'Brief Description of the Matter',
        format: 'textarea',
      },
      proceedingConfidence: {
        type: 'string',
        title: 'Are you ready to proceed with representation?',
        widget: 'yesNoMaybe',
      },
      isLitigation: {
        type: 'boolean',
        title: 'Is this a litigation matter?',
        default: false,
      },
      incidentLocation: {
        type: 'object',
        title: 'Primary incident location',
        description: "Use the button to populate from your current position or enter coordinates manually.",
        widget: 'location',
        properties: {
          latitude: { type: 'number', title: 'Latitude' },
          longitude: { type: 'number', title: 'Longitude' },
          accuracy: { type: 'number', title: 'Accuracy' },
          timestamp: { type: 'number', title: 'Timestamp' },
        },
      },
      supportingDocument: {
        type: 'string',
        title: 'Upload a supporting document',
        widget: 'file',
        description: 'Attach a document that helps describe the matter (PDF, DOCX, PNG).',
      },
      sitePhoto: {
        type: 'string',
        title: 'Capture a quick photo',
        widget: 'camera',
        description: "Snap a photo from your device's camera if relevant.",
      },
      termsAgreement: {
        type: 'boolean',
        title: 'Engagement Terms',
        widget: 'acceptance',
        description: 'I confirm the information provided is accurate and agree to secure communications.',
      },
      clientSignature: {
        type: 'string',
        title: 'Client Signature',
        widget: 'signature',
      },
    },
    dependencies: {
      isLitigation: {
        if: {
          properties: {
            isLitigation: { 'const': true },
          },
        },
        then: {
          properties: {
            courtName: {
              type: 'string',
              title: 'Court Name',
            },
            caseNumber: {
              type: 'string',
              title: 'Case Number',
            },
            opposingParty: {
              type: 'string',
              title: 'Opposing Party Name',
            },
          },
          required: ['courtName', 'opposingParty'],
        },
      },
    },
  },
  'prototype-conditional-survey': {
    id: 'prototype-conditional-survey',
    ...baseMetadata,
    title: 'Customer Survey',
    description: 'Please provide your feedback.',
    type: 'object',
    properties: {
      satisfaction: {
        type: 'string',
        title: 'Overall, how satisfied are you with our service?',
        enum: ['Very Satisfied', 'Satisfied', 'Neutral', 'Unsatisfied', 'Very Unsatisfied'],
      },
      provideFeedback: {
        type: 'boolean',
        title: 'Do you want to provide detailed feedback?',
        default: false,
      },
    },
    dependencies: {
      provideFeedback: {
        if: {
          properties: { provideFeedback: { 'const': true } },
        },
        then: {
          properties: {
            feedbackType: {
              type: 'string',
              title: 'What type of feedback do you have?',
              enum: ['Comment', 'Suggestion', 'Complaint'],
            },
            feedbackText: {
              type: 'string',
              title: 'Please provide your feedback',
              format: 'textarea',
            },
          },
          required: ['feedbackText'],
        },
      },
    },
  },
  'prototype-complex-order': {
    id: 'prototype-complex-order',
    ...baseMetadata,
    title: 'Product Order Form',
    description: 'Place your order here.',
    type: 'object',
    required: ['customerName', 'items'],
    properties: {
      customerName: {
        type: 'string',
        title: 'Customer Name',
      },
      items: {
        type: 'array',
        title: 'Order Items',
        description: 'Add products to your order.',
        minItems: 1,
        items: {
          type: 'object',
          title: 'Item',
          required: ['product', 'quantity'],
          properties: {
            product: {
              type: 'string',
              title: 'Product Name',
              enum: ['Laptop', 'Mouse', 'Keyboard', 'Monitor'],
            },
            quantity: {
              type: 'integer',
              title: 'Quantity',
              default: 1,
              minimum: 1,
            },
          },
        },
      },
      payment: {
        type: 'object',
        title: 'Payment Details',
        properties: {
          method: {
            type: 'string',
            title: 'Payment Method',
            enum: ['Credit Card', 'PayPal', 'Bank Transfer'],
          },
        },
        dependencies: {
          method: {
            oneOf: [
              {
                properties: {
                  method: { 'const': 'Credit Card' },
                  cardNumber: { type: 'string', title: 'Card Number' },
                  expiryDate: { type: 'string', title: 'Expiry Date (MM/YY)', format: 'date' },
                },
                required: ['cardNumber', 'expiryDate'],
              },
              {
                properties: {
                  method: { 'const': 'PayPal' },
                  paypalEmail: { type: 'string', title: 'PayPal Email', format: 'email' },
                },
                required: ['paypalEmail'],
              },
            ],
          },
        },
      },
    },
  },
  'prototype-incorporation': {
    id: 'prototype-incorporation',
    ...baseMetadata,
    title: 'Business Incorporation Form',
    description: 'Information required to file for incorporation.',
    type: 'object',
    required: ['companyName', 'entityType', 'stateOfIncorporation'],
    properties: {
      companyName: {
        type: 'string',
        title: 'Proposed Company Name',
      },
      entityType: {
        type: 'string',
        title: 'Entity Type',
        enum: ['LLC', 'C-Corporation', 'S-Corporation', 'Non-Profit'],
      },
      stateOfIncorporation: {
        type: 'string',
        title: 'State of Incorporation',
        default: 'Delaware',
      },
      registeredAgent: {
        type: 'object',
        title: 'Registered Agent',
        properties: {
          name: { type: 'string', title: 'Agent Name' },
          address: { type: 'string', title: 'Agent Address', format: 'textarea' },
        },
        required: ['name', 'address'],
      },
    },
    dependencies: {
      entityType: {
        oneOf: [
          {
            properties: {
              entityType: { 'const': 'LLC' },
              members: {
                type: 'array',
                title: 'Members',
                minItems: 1,
                items: {
                  type: 'object',
                  title: 'Member',
                  required: ['name', 'ownershipPercentage'],
                  properties: {
                    name: { type: 'string', title: 'Full Name' },
                    email: { type: 'string', title: 'Email', format: 'email' },
                    ownershipPercentage: { type: 'number', title: 'Ownership (%)', minimum: 0.01, maximum: 100 },
                  },
                },
              },
            },
            required: ['members'],
          },
          {
            properties: {
              entityType: { 'const': 'C-Corporation' },
              shareholders: {
                type: 'array',
                title: 'Shareholders',
                minItems: 1,
                items: {
                  type: 'object',
                  title: 'Shareholder',
                  required: ['name', 'shares'],
                  properties: {
                    name: { type: 'string', title: 'Full Name' },
                    email: { type: 'string', title: 'Email', format: 'email' },
                    shares: { type: 'integer', title: 'Number of Shares', minimum: 1 },
                  },
                },
              },
            },
            required: ['shareholders'],
          },
          {
            properties: {
              entityType: { 'const': 'S-Corporation' },
              shareholders: {
                type: 'array',
                title: 'Shareholders',
                minItems: 1,
                items: {
                  type: 'object',
                  title: 'Shareholder',
                  required: ['name', 'shares'],
                  properties: {
                    name: { type: 'string', title: 'Full Name' },
                    email: { type: 'string', title: 'Email', format: 'email' },
                    shares: { type: 'integer', title: 'Number of Shares', minimum: 1 },
                  },
                },
              },
            },
            required: ['shareholders'],
          },
          {
            properties: {
              entityType: { 'const': 'Non-Profit' },
              boardMembers: {
                type: 'array',
                title: 'Board of Directors',
                minItems: 1,
                items: {
                  type: 'object',
                  title: 'Board Member',
                  required: ['name', 'role'],
                  properties: {
                    name: { type: 'string', title: 'Full Name' },
                    email: { type: 'string', title: 'Email', format: 'email' },
                    role: { type: 'string', title: 'Role (e.g., President, Treasurer)' },
                  },
                },
              },
            },
            required: ['boardMembers'],
          },
        ],
      },
    },
  },
  'prototype-nda-generator': {
    id: 'prototype-nda-generator',
    ...baseMetadata,
    title: 'Non-Disclosure Agreement Generator',
    description: 'Generate a standard non-disclosure agreement by filling in the details below.',
    type: 'object',
    required: ['disclosingParty', 'receivingParty', 'effectiveDate', 'jurisdiction'],
    properties: {
      disclosingParty: {
        type: 'object',
        title: 'Disclosing Party',
        properties: {
          name: {
            type: 'string',
            title: 'Full Name or Company Name',
          },
          address: {
            type: 'string',
            title: 'Address',
            format: 'textarea',
          },
        },
        required: ['name', 'address'],
      },
      receivingParty: {
        type: 'object',
        title: 'Receiving Party',
        properties: {
          name: {
            type: 'string',
            title: 'Full Name or Company Name',
          },
          address: {
            type: 'string',
            title: 'Address',
            format: 'textarea',
          },
        },
        required: ['name', 'address'],
      },
      effectiveDate: {
        type: 'string',
        title: 'Effective Date',
        format: 'date',
      },
      term: {
        type: 'integer',
        title: 'Term of Confidentiality (in years)',
        default: 5,
        minimum: 1,
      },
      jurisdiction: {
        type: 'string',
        title: 'Governing Law and Jurisdiction',
        description: 'e.g., State of Delaware, USA',
      },
    },
  },
  'prototype-simple-contact': {
    id: 'prototype-simple-contact',
    ...baseMetadata,
    title: 'Contact Form',
    description: 'A simple contact form.',
    type: 'object',
    required: ['name', 'email'],
    properties: {
      name: {
        type: 'string',
        title: 'Full Name',
        description: 'Please enter your full name.',
      },
      email: {
        type: 'string',
        title: 'Email Address',
        format: 'email',
      },
      message: {
        type: 'string',
        title: 'Message',
        format: 'textarea',
      },
      newsletter: {
        type: 'boolean',
        title: 'Subscribe to newsletter',
        default: true,
      },
    },
    dependencies: {
      newsletter: {
        if: {
          properties: { newsletter: { 'const': true } },
        },
        then: {
          properties: {
            interests: {
              type: 'string',
              title: 'Areas of Interest',
              description: 'Help us tailor content for you.',
              enum: ['Technology', 'Business', 'Lifestyle', 'General News'],
            },
          },
        },
      },
    },
  },
  'prototype-user-profile': {
    id: 'prototype-user-profile',
    ...baseMetadata,
    title: 'User Profile Settings',
    description: 'Customize your profile and preferences.',
    type: 'object',
    required: ['username', 'birthDate'],
    properties: {
      username: {
        type: 'string',
        title: 'Username',
        description: 'Your public display name.',
      },
      birthDate: {
        type: 'string',
        title: 'Date of Birth',
        format: 'date',
      },
      themeColor: {
        type: 'string',
        title: 'Profile Theme Color',
        format: 'color',
        default: '#012169',
      },
      notificationVolume: {
        type: 'number',
        title: 'Notification Volume',
        widget: 'range',
        minimum: 0,
        maximum: 100,
        default: 80,
      },
      interests: {
        type: 'array',
        title: 'Interests',
        description: 'Select topics that interest you.',
        items: {
          type: 'string',
          enum: ['Technology', 'Art', 'Sports', 'Music', 'Travel'],
        },
        default: ['Technology', 'Music'],
      },
    },
  },
};

const toMetadata = (schema: FormSchema): SchemaMetadata => {
  const { properties: _properties, required: _required, dependencies: _dependencies, conditionals: _conditionals, type: _type, ...metadata } = schema;
  return metadata;
};

const toVersion = (schema: FormSchema): SchemaVersion => ({
  versionId: `${schema.id}-v${schema.version}`,
  version: schema.version,
  lastModified: schema.updatedAt,
  size: JSON.stringify(schema).length,
  isLatest: true,
});

export const prototypeSchemaList: SchemaMetadata[] = Object.values(prototypeSchemas).map(toMetadata);

export const prototypeSchemaVersions: Record<string, SchemaVersion[]> = Object.values(prototypeSchemas).reduce((acc, schema) => {
  acc[schema.id] = [toVersion(schema)];
  return acc;
}, {} as Record<string, SchemaVersion[]>);

export const getPrototypeSchema = (id: string): FormSchema | undefined => prototypeSchemas[id];

export const isPrototypeSchemaId = (id: string): boolean => Object.prototype.hasOwnProperty.call(prototypeSchemas, id);

export const listPrototypeSchemas = (): FormSchema[] => Object.values(prototypeSchemas);
